'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { locales, LOCALES, DEFAULT_LOCALE } from '@/locales';
import type { Locale, Translations } from '@/locales';

const STORAGE_KEY = 'hummini_locale';

// Bật/tắt tự động detect ngôn ngữ theo trình duyệt qua .env
// NEXT_PUBLIC_I18N_AUTO_DETECT=false → luôn dùng ngôn ngữ mặc định (DEFAULT_LOCALE, cấu hình qua NEXT_PUBLIC_DEFAULT_LOCALE)
const AUTO_DETECT_ENABLED = process.env.NEXT_PUBLIC_I18N_AUTO_DETECT !== 'false';

// Browser language code → Locale mapping
const BROWSER_LANG_MAP: Record<string, Locale> = {
  // English
  'en': 'en', 'en-us': 'en', 'en-gb': 'en', 'en-au': 'en', 'en-ca': 'en',
  // French
  'fr': 'fr', 'fr-fr': 'fr', 'fr-be': 'fr', 'fr-ca': 'fr', 'fr-ch': 'fr',
  // Spanish
  'es': 'es', 'es-es': 'es', 'es-mx': 'es', 'es-ar': 'es', 'es-co': 'es',
  // Vietnamese
  'vi': 'vi', 'vi-vn': 'vi',
  // Chinese
  'zh': 'zh', 'zh-cn': 'zh', 'zh-hans': 'zh', 'zh-tw': 'zh', 'zh-hant': 'zh', 'zh-hk': 'zh',
  // Japanese
  'ja': 'ja', 'ja-jp': 'ja',
  // Korean
  'ko': 'ko', 'ko-kr': 'ko',
  // Arabic
  'ar': 'ar', 'ar-sa': 'ar', 'ar-eg': 'ar', 'ar-ae': 'ar', 'ar-ma': 'ar',

  // Portuguese — Brazil ưu tiên cao vì lượng user lớn hơn Portugal
  'pt': 'pt', 'pt-br': 'pt', 'pt-pt': 'pt', 'pt-ao': 'pt', 'pt-mz': 'pt',
  // Russian
  'ru': 'ru', 'ru-ru': 'ru', 'ru-by': 'ru', 'ru-kz': 'ru', 'ru-ua': 'ru',
  // German
  'de': 'de', 'de-de': 'de', 'de-at': 'de', 'de-ch': 'de', 'de-li': 'de',
  // Hindi — Ấn Độ có hàng chục locale, liệt kê đủ để không bỏ sót
  'hi': 'hi', 'hi-in': 'hi',
  'bn': 'hi', 'bn-in': 'hi',   // Bengali India → fallback Hindi
  'mr': 'hi', 'mr-in': 'hi',   // Marathi
  'gu': 'hi', 'gu-in': 'hi',   // Gujarati
  'pa': 'hi', 'pa-in': 'hi',   // Punjabi India
  'te': 'hi', 'te-in': 'hi',   // Telugu
  'ta': 'hi', 'ta-in': 'hi',   // Tamil India
  'kn': 'hi', 'kn-in': 'hi',   // Kannada
  'ml': 'hi', 'ml-in': 'hi',   // Malayalam
};

function detectLocaleFromBrowser(): Locale {
  try {
    const languages = navigator.languages?.length
      ? navigator.languages
      : [navigator.language];

    for (const lang of languages) {
      // Try exact match first (e.g. "zh-CN"), then prefix (e.g. "zh")
      const exact = BROWSER_LANG_MAP[lang.toLowerCase()];
      if (exact) return exact;

      const prefix = BROWSER_LANG_MAP[lang.split('-')[0].toLowerCase()];
      if (prefix) return prefix;
    }
  } catch { /* navigator unavailable */ }

  return DEFAULT_LOCALE; // fallback: theo NEXT_PUBLIC_DEFAULT_LOCALE (hoặc English)
}

interface I18nContextValue {
  locale: Locale;
  t: Translations;
  setLocale: (locale: Locale) => void;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
      if (saved && saved in locales) {
        // User đã từng chọn thủ công → ưu tiên tuyệt đối
        setLocaleState(saved);
      } else if (AUTO_DETECT_ENABLED) {
        // Lần đầu vào & bật auto-detect → detect theo ngôn ngữ thiết bị
        setLocaleState(detectLocaleFromBrowser());
      } else {
        // Tắt auto-detect → luôn dùng ngôn ngữ mặc định từ .env
        setLocaleState(DEFAULT_LOCALE);
      }
    } catch {
      setLocaleState(AUTO_DETECT_ENABLED ? detectLocaleFromBrowser() : DEFAULT_LOCALE);
    }
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const localeInfo = LOCALES.find(l => l.code === locale);
    document.documentElement.lang = locale;
    document.documentElement.dir = localeInfo?.dir ?? 'ltr';
  }, [locale, mounted]);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    try {
      localStorage.setItem(STORAGE_KEY, newLocale);
    } catch { /* silent */ }
  }, []);

  const isRTL = LOCALES.find(l => l.code === locale)?.dir === 'rtl';

  return (
    <I18nContext.Provider value={{ locale, t: locales[locale], setLocale, isRTL }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export function interpolate(str: string, vars: Record<string, string | number>): string {
  return Object.entries(vars).reduce(
    (acc, [key, val]) => acc.replace(new RegExp(`\\{${key}\\}`, 'g'), String(val)),
    str
  );
}